/**
 * PostHog flags for the conversion test branch. Every flag off (or not
 * loaded) means the site behaves exactly as it did before the branch.
 */
export const CONVERSION_FLAGS = {
	/** Experiment A: plans and payment above the benefits list on mobile. */
	mobilePaymentFirst: "membership-modal-mobile-payment-first",
	/** Experiment B: control / after_first_use / none for the auto pop-up. */
	autoModalTiming: "membership-auto-modal-timing",
	courseSectionLock: "course-section-lock-label",
	paywallPrice: "paywall-price",
	conversionTracking: "conversion-tracking-events",
	suspendVisible: "membership-suspend-visible",
	courseWhatsNext: "course-whats-next",
	yearlyRecap: "yearly-recap",
} as const;

export type ConversionFlagKey =
	(typeof CONVERSION_FLAGS)[keyof typeof CONVERSION_FLAGS];

export type MobilePaymentFirstVariant = "control" | "test";
export type AutoModalTiming = "control" | "after_first_use" | "none";

/**
 * Whether a boolean PostHog flag is on.
 *
 * @param flagValue - Raw value from PostHog; undefined while loading or blocked
 * @returns True only for an explicit `true`
 */
export const isConversionFlagOn = (flagValue: unknown): boolean =>
	flagValue === true;

/**
 * Resolve the experiment A arm.
 *
 * @param flagValue - Raw value from PostHog
 * @returns `test` for the test arm, otherwise `control`
 */
export const resolveMobilePaymentFirstVariant = (
	flagValue: unknown,
): MobilePaymentFirstVariant => (flagValue === "test" ? "test" : "control");

/**
 * Resolve the experiment B arm.
 *
 * @param flagValue - Raw value from PostHog
 * @returns The arm key, or `control` for anything unrecognised
 */
export const resolveAutoModalTiming = (flagValue: unknown): AutoModalTiming =>
	flagValue === "after_first_use" || flagValue === "none"
		? flagValue
		: "control";

/**
 * Whether the automatic membership pop-up should open on this page load.
 *
 * The server marks the pop-up as shown when it answers, so only control
 * follows its answer. `after_first_use` opens once, after the visitor's first
 * real product action, and `none` never opens.
 *
 * @param options.timing - Experiment B arm
 * @param options.serverSaysShow - The server's `shouldShowConversionModal`
 * @param options.hasUsedProduct - The visitor has taken a real product action
 * @param options.hasShownAfterFirstUse - The after-first-use pop-up already opened
 * @returns True when the pop-up should open
 */
export const shouldOpenAutoMembershipModal = (options: {
	timing: AutoModalTiming;
	serverSaysShow: boolean;
	hasUsedProduct: boolean;
	hasShownAfterFirstUse: boolean;
}): boolean => {
	if (options.timing === "none") return false;

	if (options.timing === "after_first_use") {
		return options.hasUsedProduct && !options.hasShownAfterFirstUse;
	}

	return options.serverSaysShow;
};
